import React, { ReactElement, useCallback, useContext, useState } from 'react'
// Externals imports.
import { useDropzone } from 'react-dropzone'
// Commons imports.
import Button from '../../Common/Components/Button'
import Icon, { IconName } from '../../Common/Components/Icon'
// Contexts imports.
import { LanguageContext } from '../../Common/Contexts'
// Utilities imports.
import { LanguageBulkInsertUsers } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
// CSS imports. Commented out because of current build process.
// import './uploadStep.scss';

/**
 * Component properties type.
 */
type Props = {
    file: File | null
    setFile: (file: File | null) => void
}

/**
 * Maximum file size constant.
 */
const MAX_FILE_SIZE = 10485760

/**
 * Bulk upload step for file import.
 *
 * @param file File to upload.
 * @param setFile setter for file to upload.
 */
const UploadStep: React.FC<Props> = ({ file, setFile }: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    const [isValidFile, setIsValidFile] = useState(true)
    const [errorMessage, setErrorMessage] = useState<string>()

    // Accepted files callback.
    const onDropAccepted = useCallback((acceptedFiles: OnlyNullable<File>[]) => {
        setFile(acceptedFiles[0])
        setIsValidFile(true)
    }, [])

    // rejected files callback.
    // eslint-disable-next-line
    const onDropRejected = useCallback((rejectedFiles) => {
        setIsValidFile(false)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (rejectedFiles && rejectedFiles[0].size > MAX_FILE_SIZE) {
            setErrorMessage(language.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_MaxfilesizeError))
        } else {
            setErrorMessage(language.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_InvalidfileformatError))
        }
    }, [])

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDropAccepted,
        onDropRejected,
        multiple: false,
        maxSize: MAX_FILE_SIZE,
        accept: '.csv'
    })

    /**
     * File upload related resets.
     */
    const clearFiles = (): void => {
        setFile(null)
        setIsValidFile(true)
    }

    return (
        <div className='centered-content'>
            <div className='upload-step'>
                <div className='short-para'>
                    <div className='text'>
                        {language.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesStarttextpart1)}
                        <a className='selectable-text' href='/CCS/files/Akixi_Bulk_Add_Users_V1.0.csv' download>
                            {language.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesStarttextbutton)}
                        </a>
                        {language.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesStarttextpart2)}
                    </div>
                    <Button
                        customClass='download-button'
                        href='/CCS/files/Akixi_Bulk_Add_Users_V1.0.csv'
                        label='Download Template'
                        download
                    />
                    <div className='text top-margin'>
                        {language.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesConfirmationtext)}
                    </div>
                </div>
                {!isValidFile ? (
                    <div className='text errors-title'>
                        <p className='align-center'>{errorMessage}</p>
                    </div>
                ) : null}
                {!file ? (
                    <div className='dropzone-wrapper'>
                        <div className={`dropzone ${isDragActive ? 'dropping-file' : ''}`} {...getRootProps()}>
                            <span className='nw-pointer'></span>
                            <span className='ne-pointer'></span>

                            <input {...getInputProps()} />
                            {isDragActive ? (
                                <div className='center-element'>
                                    <Icon iconName={IconName.FOLDER} />
                                    <p>
                                        {language.get(
                                            LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesDrophere
                                        )}
                                    </p>
                                </div>
                            ) : (
                                <div className='center-element'>
                                    <Icon iconName={IconName.FOLDER} />
                                    <p>
                                        <span className='highlight-text'>
                                            {language.get(
                                                LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesChoosefile
                                            )}
                                        </span>
                                        {language.get(
                                            LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesDraganddrop
                                        )}
                                    </p>
                                </div>
                            )}

                            <span className='sw-pointer'></span>
                            <span className='se-pointer'></span>
                        </div>
                    </div>
                ) : (
                    <React.Fragment>
                        <div className='clear-float'></div>
                        <div className='file-to-process-wrapper'>
                            <div className='file-to-process-title'>
                                {language.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesFiledetails)}
                            </div>
                            <div className='file-details'>
                                <div className='file-name'>{file?.name}</div>
                                <div className='file-size'>{`(${file?.size} bytes)`}</div>
                                <div className='file-clear' onClick={clearFiles} title='Remove file'>
                                    <Icon iconName={IconName.X} />
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                )}
            </div>
        </div>
    )
}

export default UploadStep
